import axios from 'axios'
import {DealDocument} from 'common'
import {methods} from 'common/apis'
import {APIDealDocument} from 'common/apis/types'
import {
  ACH_TRANSFER_ENDPOINT,
  ZOHO_MANUAL_SIGN_GENERAL_MSA_AGREEMENT,
  ZOHO_MANUAL_SIGN_LA_AGREEMENT,
  ZOHO_SIGN_GENERAL_MSA_AGREEMENT,
  ZOHO_SIGN_GENERAL_RECALL_AGREEMENT,
  ZOHO_SIGN_LISTING_AGREEMENT
} from 'common/constants'
import {Collection, dealStage} from 'common/enums'
import {API_HOST_URL} from 'components/constants'
import {FirebaseInstances, getRequestHeaders, handleAPIRequest} from './helpers'
import {dealsApi} from 'lib/api'

export function createDealService(_fb: FirebaseInstances) {
  const getErrorMessage = (error: any) => {
    if (typeof error === 'string') return error
    if (error?.message) return error.message
    if (error?.msg) return error.msg
    return 'Request failed.'
  }

  const recallAgreement = (
    cancelReason: string,
    documentId: string,
    agreementType:
      | Collection.ZohoSignGeneralMsaAgreement
      | Collection.ZohoSignListingAgreement
  ) => {
    return handleAPIRequest(ZOHO_SIGN_GENERAL_RECALL_AGREEMENT, methods.POST, {
      documentId,
      agreementType,
      cancelReason
    })
  }

  const signAgreement = async ({
    agreementData,
    signMSA,
    signACH
  }: {
    agreementData: {
      consigneeName: string
      consigneeEmail: string
      consigneeId?: string
      productID?: string
      accountID?: string
      contactID?: string
      adminID?: string
    }
    signMSA?: boolean
    signACH?: boolean
  }) => {
    const endpoint = signMSA
      ? ZOHO_SIGN_GENERAL_MSA_AGREEMENT
      : signACH
      ? ACH_TRANSFER_ENDPOINT
      : ZOHO_SIGN_LISTING_AGREEMENT
    const {consigneeId, accountID, productID} = agreementData
    const APIData = signMSA
      ? {
          consignee_id: consigneeId || '',
          accountId: accountID || ''
        }
      : signACH
      ? {
          accountId: accountID || ''
        }
      : {
          consignee_id: consigneeId || '',
          productID: productID || ''
        }
    try {
      const headers = await getRequestHeaders()
      const rawResponse = await axios.post(
        `${API_HOST_URL}${endpoint}`,
        {...APIData},
        {headers}
      )
      const message = rawResponse.data.msg
      const statusCode = rawResponse.data.code
      return {response: rawResponse, message, statusCode}
    } catch (err: any) {
      const {response} = err
      const {data, status} = response || {}
      return {
        error: data?.msg || data?.data || getErrorMessage(err),
        errorCode: status
      }
    }
  }

  const manualSignAgreement = async ({
    accountID,
    productID,
    signMSA
  }: {
    accountID?: string
    productID?: string
    signMSA?: boolean
  }) => {
    const endpoint = signMSA
      ? ZOHO_MANUAL_SIGN_GENERAL_MSA_AGREEMENT
      : ZOHO_MANUAL_SIGN_LA_AGREEMENT
    const APIData = signMSA
      ? {accountId: accountID || ''}
      : {productId: productID || ''}
    try {
      const headers = await getRequestHeaders()
      const rawResponse = await axios.post(
        `${API_HOST_URL}${endpoint}`,
        {...APIData},
        {headers}
      )
      const message = rawResponse.data.msg
      const statusCode = rawResponse.data.code
      return {response: rawResponse, message, statusCode}
    } catch (err: any) {
      const {response} = err
      const {data, status} = response || {}
      return {
        error: data?.msg || data?.data || getErrorMessage(err),
        errorCode: status
      }
    }
  }

  const updateDeal = async (stage: dealStage, dealId: string) => {
    try {
      await dealsApi.updateStage(dealId, stage)
      return {success: true}
    } catch (error) {
      return {error}
    }
  }

  const createDeal = async (dealObject: APIDealDocument) => {
    try {
      const result = await dealsApi.create<DealDocument>(dealObject)
      return {
        response: result,
        message: 'Deal created successfully.',
        dealId: (result as DealDocument)?.dealId
      }
    } catch (err: any) {
      return {
        error: err?.response?.data?.msg || err,
        errorCode: err?.response?.status
      }
    }
  }

  const getUserDeals = (
    updateState: (e: any) => void,
    cancelCallback: (e: Error) => void,
    isSuperAdminOrManager: boolean,
    accountIdList: Array<string>,
    options?: {
      includeDeleted?: boolean
    }
  ) => {
    try {
      let inFlight = false
      let isActive = true

      const fetchDeals = async () => {
        if (!isActive || inFlight) return

        try {
          inFlight = true
          const params: Record<string, string> = {}
          if (!isSuperAdminOrManager && accountIdList.length > 0) {
            params.sellerAccountIds = accountIdList.join(',')
          }
          if (options?.includeDeleted) {
            params.includeDeleted = 'true'
          }
          const result = await dealsApi.list<{
            deals: DealDocument[]
            total: number
          }>(params)
          const deals = (result as any)?.deals || []
          if (isActive) {
            updateState(Array.isArray(deals) ? deals : [])
          }
        } catch (e) {
          if (isActive) {
            cancelCallback(e as Error)
          }
        } finally {
          inFlight = false
        }
      }

      fetchDeals()
      const intervalId = window.setInterval(() => {
        if (document.visibilityState !== 'hidden') {
          fetchDeals()
        }
      }, 5000)
      const handleWindowFocus = () => {
        fetchDeals()
      }
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          fetchDeals()
        }
      }
      window.addEventListener('focus', handleWindowFocus)
      document.addEventListener('visibilitychange', handleVisibilityChange)

      return {
        unSubscribe: () => {
          isActive = false
          window.clearInterval(intervalId)
          window.removeEventListener('focus', handleWindowFocus)
          document.removeEventListener(
            'visibilitychange',
            handleVisibilityChange
          )
        }
      }
    } catch (error) {
      return {error}
    }
  }

  return {
    recallAgreement,
    signAgreement,
    manualSignAgreement,
    updateDeal,
    createDeal,
    getUserDeals
  }
}
